<script setup lang="ts">
import { Home, Inbox, LogOut, ServerCog, ShieldCheck, Users } from 'lucide-vue-next';
import { useAuth } from '@/composables/useAuth';
import { useFamily } from '@/composables/useFamily';
import { useNavigation } from '@/composables/useNavigation';

const { navigate, route } = useNavigation();
const { setMailboxViewer } = useFamily();
const { isAuthenticated, logout, user } = useAuth();

const items = [
  { key: 'family', label: 'Familie', icon: Home },
  { key: 'child-home', label: 'Kind', icon: Users },
  { key: 'mailbox', label: 'Briefe', icon: Inbox },
  { key: 'parent-settings', label: 'Eltern', icon: ShieldCheck }
] as const;

function openNavigationItem(key: (typeof items)[number]['key']) {
  if (key === 'mailbox') {
    setMailboxViewer(route.value === 'child-home' || route.value === 'friend' || route.value === 'drawing' || route.value === 'voice' ? 'child' : 'parent');
  }

  navigate(key);
}

async function submitLogout() {
  await logout();
  navigate('login');
}
</script>

<template>
  <header class="sticky top-0 z-20 border-b border-orange-100 bg-heart-cream/95 backdrop-blur">
    <div class="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
      <button
        class="focus-ring flex items-center gap-3 rounded-full px-2 py-2 text-left"
        type="button"
        aria-label="Zur Familienuebersicht"
        @click="navigate('family')"
      >
        <span class="grid h-12 w-12 place-items-center overflow-hidden rounded-full bg-white shadow-sm ring-2 ring-orange-100">
          <img class="h-full w-full object-cover" src="/freundedorf-icon.svg" alt="" aria-hidden="true" />
        </span>
        <span>
          <span class="block text-2xl font-black text-heart-berry">Freundedorf</span>
          <span class="block text-xs font-bold text-heart-ink">Hier wohnen Freundschaften ❤️</span>
        </span>
      </button>

      <nav class="hidden items-center gap-2 md:flex" aria-label="Hauptnavigation">
        <button
          v-for="item in items"
          :key="item.key"
          class="focus-ring inline-flex min-h-12 items-center gap-2 rounded-full px-4 py-2 text-sm font-extrabold shadow-sm"
          :class="route === item.key ? 'bg-heart-coral text-white' : 'bg-white text-heart-ink'"
          type="button"
          :aria-label="`${item.label} oeffnen`"
          @click="openNavigationItem(item.key)"
        >
          <component
            :is="item.icon"
            class="h-5 w-5"
            :class="route === item.key ? 'text-white' : 'text-heart-leaf'"
            aria-hidden="true"
          />
          {{ item.label }}
        </button>
        <button
          v-if="user?.role === 'admin'"
          class="focus-ring inline-flex min-h-12 items-center gap-2 rounded-full px-4 py-2 text-sm font-extrabold shadow-sm"
          :class="route === 'admin' ? 'bg-heart-coral text-white' : 'bg-white text-heart-ink'"
          type="button"
          aria-label="Adminbereich oeffnen"
          @click="navigate('admin')"
        >
          <ServerCog class="h-5 w-5" :class="route === 'admin' ? 'text-white' : 'text-heart-leaf'" aria-hidden="true" />
          Admin
        </button>
        <button
          v-if="isAuthenticated"
          class="focus-ring inline-flex min-h-12 items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-extrabold text-heart-ink shadow-sm"
          type="button"
          aria-label="Abmelden"
          @click="submitLogout"
        >
          <LogOut class="h-5 w-5 text-heart-berry" aria-hidden="true" />
          Abmelden
        </button>
      </nav>
      <button
        v-if="isAuthenticated"
        class="focus-ring grid h-12 w-12 place-items-center rounded-full bg-white text-heart-berry shadow-sm md:hidden"
        type="button"
        aria-label="Abmelden"
        @click="submitLogout"
      >
        <LogOut class="h-6 w-6" aria-hidden="true" />
      </button>
    </div>
  </header>
</template>
